import {
  APP_DEFAULT_DECIMAL_PLACE,
  CALORIES_DECIMAL_PLACE,
  ENTITLEMENT_IDS,
  MEAL_MACRO_DECIMAL_PLACE,
  PLAN_TYPES,
  PORTION_DECIMAL_PLACE,
} from "config"

import { IEntitlement } from "features/payment/types"

export const getSvgFill = (isSelected: boolean) => {
  const backgroundFill = isSelected
    ? "var(--Schemes-Surface-Container-Low, #F5F7F7)"
    : "var(--White_20, rgba(255, 255, 255, 0.20))"

  const pathFill = isSelected
    ? "#023648"
    : "var(--White_60, rgba(255, 255, 255, 0.6))"

  return { backgroundFill, pathFill }
}

export const roundByDecimalPlace = (value: number, place?: number) => {
  const factor = Math.pow(10, place ?? APP_DEFAULT_DECIMAL_PLACE)
  return Math.round(value * factor) / factor
}

export const roundMealMacro = (value: number) => {
  return roundByDecimalPlace(value, MEAL_MACRO_DECIMAL_PLACE)
}

export const roundCalories = (value: number) => {
  return roundByDecimalPlace(value, CALORIES_DECIMAL_PLACE)
}

export const formatBirthedOn = (date: string) => {
  const [year, month, day] = date.split("-")
  return `${day}/${month}/${year}`
}

export const titleCase = (string: string) => {
  return string[0].toUpperCase() + string.slice(1).toLowerCase()
}

export const getErrorMessage = (error: any) => {
  return (
    error.response?.data?.message ?? error.message ?? "Something went wrong!"
  )
}

export const getSocialSignInErrorMessage = (error: any) => {
  const serverMessage = error.response?.data?.message

  if (serverMessage && serverMessage.startsWith("already_existing_resource")) {
    return "Please use the same method to sign in as the one used for creating the account"
  }

  return serverMessage ?? "Something went wrong!"
}

export function decimalToFraction(decimal: number) {
  // Handle the case where the input is an integer
  if (Number.isInteger(decimal)) {
    return { numerator: decimal, denominator: 1 }
  }

  const tolerance = 1.0e-6 // You can adjust this tolerance
  let numerator = 1
  let denominator = 1

  // Scale the decimal to remove decimal places
  let sign = Math.sign(decimal)
  decimal = Math.abs(decimal)

  while (Math.abs(decimal - numerator / denominator) > tolerance) {
    if (numerator / denominator < decimal) {
      numerator++
    } else {
      denominator++
    }
  }

  // Restore the sign
  numerator *= sign
  return { numerator, denominator }
}

export const getPortionFromServing = (servings: string) => {
  try {
    let [numerator, denominator] = servings.split("/")

    return {
      first: Number(numerator),
      second: denominator ? Number(denominator) : 1,
    }
  } catch (error) {
    console.error(error)
    return {
      first: 1,
      second: 1,
    }
  }
}

export const formatDisplayNumber = (
  value?: number | null,
  decimalPlaces = 0,
) => {
  if (!value) return 0

  const roundedValue = roundByDecimalPlace(value, decimalPlaces)

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimalPlaces,
  }).format(roundedValue)
}

export const getDisplayCalories = (calories?: number | null) => {
  return formatDisplayNumber(calories, CALORIES_DECIMAL_PLACE)
}

export const getDisplayMealMacro = (grams?: number | null) => {
  return formatDisplayNumber(grams, MEAL_MACRO_DECIMAL_PLACE)
}

export const getDisplayPortion = (portion?: number | null) => {
  return formatDisplayNumber(portion, PORTION_DECIMAL_PLACE)
}

export const getTrimmedDecimalPlaces = (
  value: number,
  maxDecimalPlaces: number,
) => {
  if (!maxDecimalPlaces) return 0

  const rounded = roundByDecimalPlace(value, maxDecimalPlaces)
  const decimalPart = rounded.toString().split(".")[1]

  return Math.min(decimalPart?.length ?? 0, maxDecimalPlaces)
}

export const is404Response = (error: any) => {
  return error.response?.status === 404
}

export const isAuthError = (error: any) => {
  return [401, 403].includes(error?.response?.status)
}

export const feetInchesToFeet = (feet: number, inches: number) => {
  return roundByDecimalPlace(feet + inches / 12)
}

export const feetToFeetInches = (decimalFeet: number) => {
  // Get the whole number of feet
  const feet = Math.floor(decimalFeet)
  // Get the decimal part and convert it to inches
  const inches = Math.round((decimalFeet - feet) * 12)

  return { feet, inches }
}

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

export const snakeCaseToCamelCase = (str: string) => {
  return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
}

const getCamelCaseObject = (payload: any) => {
  return Object.keys(payload).reduce((acc, key) => {
    let value = payload[key]

    if (Array.isArray(value)) {
      value = value.map((item) => {
        if (typeof item === "object") {
          return getCamelCaseObject(item)
        }
        return item
      })
    }

    if (typeof value === "object") {
      value = getCamelCaseObject(value)
    }

    return {
      ...acc,
      [camelToSnakeCase(key)]: value,
    }
  }, {})
}

export const convertResponseToCamelCase = (response: any): any => {
  if (Array.isArray(response)) {
    return response.map((item) => getCamelCaseObject(item))
  }

  return getCamelCaseObject(response)
}

export const isDigitsOnly = (value: string) => /^[0-9]+$/.test(value)

export const stripNonDigits = (input: string): string =>
  input.replace(/[^0-9]/g, "")

export const getBase64 = (
  file: File | Blob,
  callback: (url: string) => void,
) => {
  const reader = new FileReader()
  reader.addEventListener("load", () => callback(reader.result as string))
  reader.readAsDataURL(file)
}

export const checkIsBasePlan = (planType: string) => {
  if (!planType) return false

  return [PLAN_TYPES.FREE].includes(planType)
}

export const checkHasAIEntitlement = (entitlements: IEntitlement[]) => {
  return entitlements.some(
    (entitlement) => entitlement.id === ENTITLEMENT_IDS.AI_FOOD_SCANNING,
  )
}

export const checkHasScanBarcodeScanningEntitlement = (
  entitlements: IEntitlement[],
) => {
  return entitlements.some(
    (entitlement) => entitlement.id === ENTITLEMENT_IDS.BARCODE_SCANNING,
  )
}

export const checkIsWebPayment = (entitlement: IEntitlement[]) => {
  const STRIPE_PAYMENT = "STRIPE"
  return entitlement.some((entitlement) => entitlement.store === STRIPE_PAYMENT)
}
